<template>
  <div :class="$style.base">
    <BaseVisualBanner
      :heading="$t('marketing.insights.marketing_area.title')"
      :description="$t('marketing.insights.marketing_area.description')"
      imagePath="/img/marketing/banner.svg"
    />
    <div
      v-if="showRequestTrial"
      :class="$style.upgrade"
    >
      <div v-test="'request-trial-section'">
        <BaseGrid container>
          <BaseGrid :size="$screen === 'l' ? 6 : 12">
            <div>
              <BaseText
                bold
                v-test="'requested-text'"
              >
                {{ bottomHeader }}
              </BaseText>
              <BaseText>{{ bottomDesc }}</BaseText>
            </div>
          </BaseGrid>
          <BaseGrid
            :size="$screen === 'l' ? 6 : 12"
            alignCenter
            :alignRight="$screen === 'l'"
            v-test="'request-trial-button-section'"
          >
            <div
              :class="[
                $style.request,
                {
                  [$style.largeScreen]: $screen === 'l'
                }
              ]"
            >
              <BaseButton
                :loading="isLoading"
                @click="onBtnClick"
                v-test="'request-trial'"
              >
                {{ marketingTrialEndsAt
                    ? $t('global.actions.upgrade')
                    : $t(
                        'marketing.insights.marketing_area.request_trial.request_free_trial'
                      ) }}
              </BaseButton>
            </div>
          </BaseGrid>
        </BaseGrid>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import { brandName } from '@/helpers/brand-name';
import onUpgradeButtonClick from '@/modules/_shared/subscription/on-upgrade-button-click';
import { useCompanyStore } from '@/stores/company';
import { useLocalizationStore } from '@/stores/localization';
import { useUserStore } from '@/stores/user';

export default defineComponent({
  props: {
    showRequestTrial: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const { handleUpgradeButtonClick } = onUpgradeButtonClick();
    return {
      handleUpgradeButtonClick,
    };
  },
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    ...mapState(useCompanyStore, ['company']),
    ...mapState(useUserStore, ['user', 'hasFeatureFlag']),
    ...mapState(useLocalizationStore, ['language']),
    marketingTrialEndsAt() {
      return this.company.marketingTrialEndsAt;
    },
    bottomHeader() {
      return this.$t(
        `marketing.insights.marketing_area.request_trial.${this.marketingTrialEndsAt ? 'title_upgrade' : 'title'}`,
      );
    },
    bottomDesc() {
      return this.$t(
        `marketing.insights.marketing_area.request_trial.${this.marketingTrialEndsAt ? 'description_upgrade' : 'description'}`,
        brandName(),
      );
    },
  },
  methods: {
    onBtnClick() {
      if (this.marketingTrialEndsAt) {
        this.handleUpgradeButtonClick();
      } else {
        this.onRequestTrial();
      }
    },
    onRequestTrial() {
      this.isLoading = true;

      const input = {
        moduleName: 'marketing',
      };

      this.$apollo
        .mutate({
          mutation: gql`
            mutation activateTrial($input: ActivateTrialInput!) {
              activateTrial(input: $input) {
                trialEndsAt
                errors
              }
            }
          `,
          variables: {
            input,
          },
        })
        .then(() => {
          const { getUser } = useUserStore();
          getUser().finally(() => {
            this.isLoading = false;
          });
        })
        .catch((error) => {
          this.isLoading = false;
          return error;
        });
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.request {
  display: flex;
  align-items: center;

  &:not(.largeScreen) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.upgrade {
  padding: $spacing * 1.5;
  border-radius: $radius;
  background-color: var(--success-softer);
}
</style>
